import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Calendar, Clock, User, Tag } from 'lucide-react';
import { useEffect } from 'react';
import styles from './Components/CSS/BlogsCommonPage.module.css';
import SimilarBlogs from './SimilarBlogs';
import { useBlog, useSimilarBlogs } from '../hooks/useBlogs';
import { Helmet } from 'react-helmet';

const BlogReader = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { blog: ebook, isLoading, error } = useBlog(id);
  const { similarBlogs } = useSimilarBlogs(ebook?.id);

  // SEO Meta Tags Update
  useEffect(() => {
    if (ebook) {
      // Update document title
      document.title = ebook.metaTitle || ebook.title || 'Blog Post';
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', ebook.metaDescription || '');
      } else {
        const newMetaDescription = document.createElement('meta');
        newMetaDescription.name = 'description';
        newMetaDescription.content = ebook.metaDescription || '';
        document.head.appendChild(newMetaDescription);
      }

      // Update meta keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', ebook.metaKeywords || '');
      } else if (ebook.metaKeywords) {
        const newMetaKeywords = document.createElement('meta');
        newMetaKeywords.name = 'keywords';
        newMetaKeywords.content = ebook.metaKeywords;
        document.head.appendChild(newMetaKeywords);
      }

      // Open Graph tags
      updateMetaTag('property', 'og:title', ebook.metaTitle || ebook.title);
      updateMetaTag('property', 'og:description', ebook.metaDescription || '');
      updateMetaTag('property', 'og:image', ebook.featuredImage || '');
      updateMetaTag('property', 'og:url', window.location.href);
      updateMetaTag('property', 'og:type', 'article');
      updateMetaTag('property', 'og:site_name', 'Suno Kahaniyan');

      // Twitter Card tags
      updateMetaTag('name', 'twitter:card', 'summary_large_image');
      updateMetaTag('name', 'twitter:title', ebook.metaTitle || ebook.title);
      updateMetaTag('name', 'twitter:description', ebook.metaDescription || '');
      updateMetaTag('name', 'twitter:image', ebook.featuredImage || '');

      // Article specific meta tags
      updateMetaTag('property', 'article:author', ebook.author?.name || '');
      updateMetaTag('property', 'article:published_time', ebook.createdAt || '');
      updateMetaTag('property', 'article:modified_time', ebook.updatedAt || '');
      updateMetaTag('property', 'article:section', ebook.category || '');

      // Canonical URL
      updateCanonicalUrl(window.location.href);

      // JSON-LD Structured Data
      updateStructuredData(ebook);
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Suno Kahaniyan - Stories That Matter';
    };
  }, [ebook]);

  const updateMetaTag = (attribute, value, content) => {
    if (!content) return;
    
    const existingTag = document.querySelector(`meta[${attribute}="${value}"]`);
    if (existingTag) {
      existingTag.setAttribute('content', content);
    } else {
      const newTag = document.createElement('meta');
      newTag.setAttribute(attribute, value);
      newTag.content = content;
      document.head.appendChild(newTag);
    }
  };

  const updateCanonicalUrl = (url) => {
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.href = url;
    } else {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      document.head.appendChild(canonical);
    }
  };

  const updateStructuredData = (blog) => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": blog.metaDescription,
      "image": blog.featuredImage,
      "author": {
        "@type": "Person",
        "name": blog.author?.name || "Unknown Author"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Suno Kahaniyan",
        "logo": {
          "@type": "ImageObject",
          "url": "https://sunokahaniyan.com/images/logo/newlogo.png",
        }
      },
      "datePublished": blog.createdAt,
      "dateModified": blog.updatedAt,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      },
      "articleSection": blog.category,
      "keywords": blog.metaKeywords,
      "wordCount": blog.content ? blog.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0,
      "url": window.location.href
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  };

  const handleGoBack = () => {
    navigate('/blogs');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatReadTime = (content) => {
    if (!content) return '1 min read';
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readTime = Math.ceil(words / 200);
    return `${readTime} min read`;
  };

  const formatDateForSchema = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString();
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Something went wrong</h2>
          <p>{error.message || 'Failed to load blog post'}</p>
          <button onClick={handleGoBack} className={styles.backButton}>
            <ArrowLeft size={20} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Article not found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
          <button onClick={handleGoBack} className={styles.backButton}>
            <ArrowLeft size={20} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <Helmet>
      <title>{ebook.title} - SunoKahaniyan</title>
      <meta name="description" content={ebook.metaDescription} />
      <meta name="keywords" content={ebook.metaKeywords} />
    </Helmet>
    <div className={styles.container}>
      {/* Navigation Header with Breadcrumbs */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <button 
            onClick={handleGoBack} 
            className={styles.backButton}
            aria-label="Go back to blogs"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </header>

      {/* SEO Optimized Title and Description */}
      <div className={styles.titleSubtitleWrapper}>
        <h1 className={styles.title} itemProp="headline">
          {ebook.title}
        </h1>
        {ebook.metaDescription && (
          <p className={styles.subtitle} itemProp="description">
            {ebook.metaDescription}
          </p>
        )}
      </div>

      {/* Featured Image with SEO attributes */}
      {ebook.featuredImage && (
        <div className={styles.heroImage}>
          <img 
            src={ebook.featuredImage} 
            alt={ebook.title}
            className={styles.featuredImage}
            itemProp="image"
            loading="eager"
            width="800"
            height="400"
          />
        </div>
      )}

      {/* Main Content with Schema.org markup */}
      <main className={styles.main}>
        <article 
          className={styles.article}
          itemScope 
          itemType="https://schema.org/BlogPosting"
        >
          {/* Article Header with Rich Snippets */}
          <header className={styles.articleHeader}>
            <div className={styles.articleMeta}>
              <div className={styles.authorSection}>
                <div className={styles.authorInfo}>
                  <div className={styles.authorName} itemProp="author" itemScope itemType="https://schema.org/Person">
                    <User size={16} />
                    <span itemProp="name">By {ebook.author?.name || 'Unknown Author'}</span>
                  </div>
                  <div className={styles.metaDetails}>
                    <time 
                      className={styles.publishDate}
                      dateTime={formatDateForSchema(ebook.createdAt)}
                      itemProp="datePublished"
                    >
                      <Calendar size={16} />
                      {formatDate(ebook.createdAt)}
                    </time>
                    {ebook.updatedAt !== ebook.createdAt && (
                      <time 
                        className={styles.updateDate}
                        dateTime={formatDateForSchema(ebook.updatedAt)}
                        itemProp="dateModified"
                      >
                        Updated: {formatDate(ebook.updatedAt)}
                      </time>
                    )}
                    <span className={styles.readTime}>
                      <Clock size={16} />
                      {formatReadTime(ebook.content)}
                    </span>
                    {ebook.category && (
                      <span className={styles.category} itemProp="articleSection">
                        <Tag size={16} />
                        {ebook.category}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.articleStats}>
                <div className={styles.statItem}>
                  <Eye size={16} />
                  <span>{ebook.views || 0} views</span>
                </div>
              </div>
            </div>
          </header>

          {/* Article Content with proper markup */}
          <div className={styles.contentWrapper}>
            <div 
              className={styles.content}
              itemProp="articleBody"
              dangerouslySetInnerHTML={{ __html: ebook.content }}
            />
          </div>

          {/* Hidden schema.org data */}
          <div style={{ display: 'none' }}>
            <span itemProp="publisher" itemScope itemType="https://schema.org/Organization">
              <span itemProp="name">Suno Kahaniyan</span>
              <span itemProp="logo" itemScope itemType="https://schema.org/ImageObject">
                <meta itemProp="url" content="https://sunokahaniyan.com/images/logo/newlogo.png" />
              </span>
            </span>
            <meta itemProp="mainEntityOfPage" content={window.location.href} />
            <meta itemProp="url" content={window.location.href} />
            {ebook.metaKeywords && <meta itemProp="keywords" content={ebook.metaKeywords} />}
            <meta itemProp="wordCount" content={ebook.content ? ebook.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0} />
          </div>
        </article>
      </main>

      {/* Similar Blogs Section */}
      {similarBlogs && similarBlogs.length > 0 && (
        <section aria-label="Related Articles">
          <SimilarBlogs blogs={similarBlogs} />
        </section>
      )}
    </div>
    </>
  );
};

export default BlogReader;